import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Paper,
  Alert,
  InputAdornment,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Build as ServiceIcon,
  Security as MOTIcon,
  Speed as QuickIcon,
  LocalOffer as PriceIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { useBooking } from '../contexts/BookingContext.jsx';
import { apiService } from '../services/api.js';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { 
    setVehicleReg, 
    setVehicleData, 
    setPostcode, 
    setCoordinates,
    setLoading,
    setError,
    clearError,
    isLoading,
    error,
    setAvailableProducts,
    setAvailableAddons,
    setSelectedProducts,
    setSelectedAddons,
    selectedProducts,
    selectedAddons,
    availableProducts,
    availableAddons
  } = useBooking();
  
  const [formData, setFormData] = useState({
    vehicleReg: '',
    postcode: '',
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [modalTab, setModalTab] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);

  // Handle navigation state to pre-populate form fields
  useEffect(() => {
    // Clear errors when home page loads
    clearError();
    
    if (location.state) {
      const { vehicleReg, postcode } = location.state;
      if (vehicleReg || postcode) {
        setFormData(prev => ({
          ...prev,
          vehicleReg: vehicleReg || prev.vehicleReg,
          postcode: postcode || prev.postcode,
        }));
      }
    }
  }, [location.state]);

  const fetchServicesData = async () => {
    try {
      setProductsLoading(true);
      const [productsResponse, addonsResponse] = await Promise.all([
        apiService.getProducts({ status: 'active' }),
        apiService.getAddons({ status: 'active' })
      ]);
      
      setAvailableProducts(productsResponse.data);
      setAvailableAddons(addonsResponse.data);
      
     
    } catch (err) {
      console.error('Failed to fetch services data:', err);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleServiceFieldClick = async () => {
    // Open modal immediately
    setShowServiceModal(true);
    
    // Always fetch fresh data when popup opens to ensure active status and latest data
    await fetchServicesData();
  };

  const validateForm = () => {
    const errors = {};
    
    // Vehicle registration validation
    const regPattern = /^[A-Z]{2}[0-9]{2}\s?[A-Z]{3}$|^[A-Z][0-9]{1,3}\s?[A-Z]{3}$|^[A-Z]{3}\s?[0-9]{1,3}[A-Z]$/;
    if (!formData.vehicleReg.trim()) {
      errors.vehicleReg = t('validation.required');
    } else if (!regPattern.test(formData.vehicleReg.replace(/\s/g, '').toUpperCase())) {
      errors.vehicleReg = t('validation.invalidReg');
    }
    
    // Postcode validation
    const postcodePattern = /^[A-Z]{1,2}[0-9R][0-9A-Z]?\s?[0-9][A-Z]{2}$/;
    if (!formData.postcode.trim()) {
      errors.postcode = t('validation.required');
    } else if (!postcodePattern.test(formData.postcode.replace(/\s/g, '').toUpperCase())) {
      errors.postcode = t('validation.invalidPostcode');
    }
    
    // Service selection validation
    if (selectedProducts.length === 0 && selectedAddons.length === 0) {
      errors.services = 'Please select at least one service';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value.toUpperCase();
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Clear booking error
    if (error) {
      clearError();
    }
  };

  const handleProductSelect = (product) => {
    const isSelected = selectedProducts.some(p => p.product_id === product.id);
    
    if (isSelected) {
      // Remove product
      setSelectedProducts(selectedProducts.filter(p => p.product_id !== product.id));
    } else {
      // Add product
      const newProduct = {
        product_id: product.id,
        is_enabled: true,
        product: product
      };
      setSelectedProducts([...selectedProducts, newProduct]);
    }
  };

  const handleAddonSelect = (addon) => {
    const isSelected = selectedAddons.some(a => a.addon_id === addon.id);
    
    if (isSelected) {
      // Remove addon
      setSelectedAddons(selectedAddons.filter(a => a.addon_id !== addon.id));
    } else {
      // Add addon
      const newAddon = {
        addon_id: addon.id,
        is_enabled: true,
        addon: addon
      };
      setSelectedAddons([...selectedAddons, newAddon]);
    }
  };

  const isProductSelected = (productId) => {
    return selectedProducts.some(p => p.product_id === productId);
  };

  const isAddonSelected = (addonId) => {
    return selectedAddons.some(a => a.addon_id === addonId);
  };


  const getSelectedServicesText = () => {
    const totalServices = selectedProducts.length + selectedAddons.length;
    if (totalServices === 0) return "Select services";
    if (totalServices === 1) {
      const service = selectedProducts[0] || selectedAddons[0];
      const serviceName = service.product?.name || service.addon?.name;
      return serviceName;
    }
    return `${totalServices} services selected`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    clearError();

    try {
      // Look up vehicle information
      const vehicleResponse = await apiService.lookupVehicle(
        formData.vehicleReg.replace(/\s/g, '')
      );
      
      // Update booking context (coordinates will be resolved by backend)
      setVehicleReg(formData.vehicleReg);
      setVehicleData(vehicleResponse.data);
      setPostcode(formData.postcode);
      
      // Navigate to garage selection
      navigate('/garages');
      
    } catch (error) {
      console.error('Search error:', error);
      
      if (error.response?.status === 404) {
        setError('Vehicle not found. Please check the registration number.');
      } else if (error.response?.status === 502) {
        setError(error.response.data?.detail || 'Vehicle lookup is temporarily unavailable. Please try again.');
      } else if (error.message.includes('postcode')) {
        setError('Invalid postcode. Please check and try again.');
      } else {
        setError('Unable to find garages in your area. Please try again.');
      }
      
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <SearchIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: t('home.features.easy'),
      description: t('home.features.easyDesc'),
    },
    {
      icon: <LocationIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: t('home.features.local'),
      description: t('home.features.localDesc'),
    },
    {
      icon: <CheckIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: t('home.features.instant'),
      description: t('home.features.instantDesc'),
    },
  ];

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: 'calc(100vh - 64px)' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography 
            variant="h1" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              mb: 3,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              color: 'primary.main',
              lineHeight: 1.2,
            }}
          >
            {t('home.title')}
          </Typography>
          
          <Typography 
            variant="h4" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 4, md: 6 }, 
              maxWidth: 700, 
              mx: 'auto',
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              fontWeight: 400,
              lineHeight: 1.4,
            }}
          >
            {t('home.subtitle')}
          </Typography>
        </Box>

        {/* Search Form */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', lg: '1.2fr 0.8fr' }, 
          gap: { xs: 4, lg: 6 }, 
          alignItems: 'center', 
          mb: { xs: 8, md: 10 } 
        }}>
          <Box>
            <Card elevation={3} sx={{ p: 2 }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
                  Find Local Garages & Services
                </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
              
              
              <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  fullWidth
                  label={t('home.vehicleReg')}
                  placeholder={t('home.vehicleRegPlaceholder')}
                  value={formData.vehicleReg}
                  onChange={handleInputChange('vehicleReg')}
                  error={!!validationErrors.vehicleReg}
                  helperText={validationErrors.vehicleReg}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CarIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                />
                
                {/* Service Selection Dropdown */}
                <TextField
                  fullWidth
                  label="Services"
                  placeholder="Select your services"
                  value={getSelectedServicesText()}
                  onClick={handleServiceFieldClick}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <ExpandMoreIcon color="action" />
                      </InputAdornment>
                    ),
                    startAdornment: (
                      <InputAdornment position="start">
                        <ServiceIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  error={!!validationErrors.services}
                  helperText={validationErrors.services}
                  disabled={isLoading}
                  sx={{ cursor: 'pointer' }}
                />
                
                {/* Selected Services Summary */}
                {(selectedProducts.length > 0 || selectedAddons.length > 0) && (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedProducts.map((selected) => (
                        <Chip
                          key={selected.product_id}
                          label={selected.product.name}
                          color="primary"
                          size="small"
                        />
                      ))}
                      {selectedAddons.map((selected) => (
                        <Chip
                          key={selected.addon_id}
                          label={selected.addon.name}
                          color="secondary"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                
                <TextField
                  fullWidth
                  label={t('home.postcode')}
                  placeholder={t('home.postcodePlaceholder')}
                  value={formData.postcode}
                  onChange={handleInputChange('postcode')}
                  error={!!validationErrors.postcode}
                  helperText={validationErrors.postcode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isLoading}
                  sx={{ 
                    py: 1.5,
                    '&.Mui-disabled': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      opacity: 0.8
                    }
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    'Find Garages'
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
        
          <Box>
            <Box sx={{ pl: { lg: 4 }, mt: { xs: 4, lg: 0 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
                {features.slice(0, 3).map((feature, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: 3,
                      p: { xs: 2, md: 3 },
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                        borderColor: 'primary.light',
                      }
                    }}
                  >
                    <Box sx={{ 
                      minWidth: { xs: 40, md: 48 },
                      height: { xs: 40, md: 48 },
                      borderRadius: '50%',
                      bgcolor: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'primary.contrastText'
                    }}>
                      {React.cloneElement(feature.icon, { sx: { fontSize: { xs: 20, md: 24 } } })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 1,
                          fontSize: { xs: '1rem', md: '1.1rem' },
                          color: 'text.primary'
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.875rem', md: '0.95rem' },
                          lineHeight: 1.5
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

      {/* Benefits Section */}
      <Box sx={{ 
        mb: 6, 
        bgcolor: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        py: { xs: 4, md: 6 }, 
        px: { xs: 2, md: 4 }, 
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            textAlign: 'center', 
            mb: 2,
            fontWeight: 'bold',
            color: 'primary.main',
            fontSize: { xs: '1.75rem', md: '2.125rem' }
          }}
        >
          Why Choose Our Platform?
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            textAlign: 'center', 
            mb: { xs: 4, md: 6 },
            color: 'text.secondary',
            maxWidth: 600,
            mx: 'auto',
            fontSize: { xs: '1rem', md: '1.25rem' }
          }}
        >
          Connect with trusted garages and book with confidence
        </Typography>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, 
          gap: { xs: 3, md: 4 }, 
          maxWidth: 1000, 
          mx: 'auto' 
        }}>
          {[
            {
              icon: <CheckIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
              title: 'Transparent Pricing',
              description: 'No hidden fees, see all costs upfront before booking'
            },
            {
              icon: <MOTIcon sx={{ fontSize: 40, color: 'success.main' }} />,
              title: 'Approved Garages',
              description: 'All garages are DVSA approved and thoroughly vetted'
            },
            {
              icon: <QuickIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
              title: 'Instant Booking',
              description: 'Book appointments 24/7 online with immediate confirmation'
            },
            {
              icon: <SearchIcon sx={{ fontSize: 40, color: 'info.main' }} />,
              title: 'Easy Comparison',
              description: 'Compare prices, services and reviews in one place'
            },
            {
              icon: <LocationIcon sx={{ fontSize: 40, color: 'error.main' }} />,
              title: 'Local Convenience',
              description: 'Find trusted garages near you with accurate availability'
            },
            {
              icon: <ServiceIcon sx={{ fontSize: 40, color: 'info.main' }} />,
              title: 'Full Service Range',
              description: 'From MOT tests to major services, all in one platform'
            },
          ].map((benefit, index) => (
            <Paper 
              key={index}
              elevation={2}
              sx={{ 
                p: { xs: 3, md: 4 }, 
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  bgcolor: benefit.icon.props.sx?.color || 'primary.main',
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.3s ease'
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6,
                  borderColor: 'primary.light',
                  '&::before': {
                    transform: 'scaleX(1)'
                  }
                },
              }}
            >
              <Box sx={{ 
                width: { xs: 60, md: 70 },
                height: { xs: 60, md: 70 },
                borderRadius: '50%', 
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                {benefit.icon}
              </Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                lineHeight: 1.3
              }}>
                {benefit.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{
                lineHeight: 1.6,
                fontSize: { xs: '0.9rem', md: '1rem' },
                maxWidth: '280px'
              }}>
                {benefit.description}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
      
      {/* Service Selection Modal */}
      <Dialog 
        open={showServiceModal} 
        onClose={() => setShowServiceModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, maxHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Typography variant="h5" component="div">
            Select Your Services
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose the services you need for your vehicle
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ px: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs 
              value={modalTab} 
              onChange={(e, newValue) => setModalTab(newValue)}
              variant="fullWidth"
            >
              <Tab label="Main Services" />
              <Tab label="Add-ons" />
            </Tabs>
          </Box>
          
          {productsLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading available services...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ minHeight: 400 }}>
              {/* Products Tab */}
              {modalTab === 0 && (
                <List sx={{ px: 0 }}>
                  {availableProducts.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No products available at the moment
                      </Typography>
                    </Box>
                  ) : (
                    availableProducts.map((product) => {
                    const isSelected = isProductSelected(product.id);
                    return (
                      <ListItem key={product.id} sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'stretch', sm: 'flex-start' }, 
                            mb: 1 
                          }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                {product.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {product.description}
                              </Typography>
                              <Chip 
                                label={product.category} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </Box>
                            <Box sx={{ ml: 2, textAlign: 'right' }}>
                              <Button
                                variant={isSelected ? "contained" : "outlined"}
                                size="small"
                                onClick={() => handleProductSelect(product)}
                                startIcon={isSelected ? <CheckIcon /> : <AddIcon />}
                              >
                                {isSelected ? 'Selected' : 'Select'}
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </ListItem>
                    );
                  }))}
                </List>
              )}
              
              {/* Addons Tab */}
              {modalTab === 1 && (
                <List sx={{ px: 0 }}>
                  {availableAddons.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No add-ons available at the moment
                      </Typography>
                    </Box>
                  ) : (
                    availableAddons.map((addon) => {
                    const isSelected = isAddonSelected(addon.id);
                    return (
                      <ListItem key={addon.id} sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'stretch', sm: 'flex-start' }, 
                            mb: 1 
                          }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                {addon.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {addon.description}
                              </Typography>
                              {addon.is_free && (
                                <Chip 
                                  label="Free" 
                                  size="small" 
                                  color="success" 
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            <Box sx={{ ml: 2, textAlign: 'right' }}>
                              <Button
                                variant={isSelected ? "contained" : "outlined"}
                                size="small"
                                onClick={() => handleAddonSelect(addon)}
                                startIcon={isSelected ? <CheckIcon /> : <AddIcon />}
                                color={addon.is_free ? "success" : "primary"}
                              >
                                {isSelected ? 'Selected' : 'Select'}
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </ListItem>
                    );
                  }))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: { xs: 2, md: 3 }, 
          bgcolor: 'grey.50', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {selectedProducts.length + selectedAddons.length} service{(selectedProducts.length + selectedAddons.length) !== 1 ? 's' : ''} selected
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, sm: 1 },
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: '200px' }
          }}>
            <Button 
              onClick={() => setShowServiceModal(false)} 
              variant="contained"
              disabled={(selectedProducts.length + selectedAddons.length) === 0}
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: '140px' }
              }}
            >
              Confirm Selection
            </Button>
            <Button 
              onClick={() => setShowServiceModal(false)} 
              variant="outlined"
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: '90px' }
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};

export default HomePage;